"use client"
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link as LinkIcon } from 'lucide-react';
import { footnotesLinkWithOrigin } from './blocks/detailsHashSync';

// Footnote definitions open in this modal when the in-text number is
// tapped/clicked (see FootnoteRef in Markdown.tsx). The card is deliberately
// identical to the hover tooltip's — same chrome, number marker, and
// typography — just centered over a dimmed backdrop so it's comfortable on
// touch screens, with an inconspicuous permalink at the end. Rendered in a
// portal at <body>: the fixed overlay can't live inside the reference's
// <sup> (a <div> inside <p> is invalid markup).
export default function FootnoteModal({
  identifier,
  anchorId,
  onClose,
  children,
}: {
  /** The visible footnote number (unprefixed, as printed in the text). */
  identifier: string;
  /** The hash/element id, e.g. `footnote-12` (chunk prefix included). */
  anchorId: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock page scroll behind the overlay: the card scrolls internally, and on
  // touch screens a scrollable page underneath would grab the gesture.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-3"
      onClick={onClose}
    >
      {/* Same card as the footnote tooltip in Markdown.tsx (tooltipContent). */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Footnote ${identifier}`}
        className="w-[470px] max-w-full bg-vivid-background border-[1.5px] border-black rounded-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="markdown-content flex items-start gap-[8px] max-h-[70vh] overflow-y-auto px-3 pt-4 pb-2 font-[et-book,Palatino,'Palatino_Linotype','Book_Antiqua',Georgia,serif] text-black text-[1.2rem] leading-[1.4] [&_p]:leading-[1.4] [&_li]:leading-[1.4] [&_p]:my-[0.5em] [&_p]:text-[1.2rem] [&_ol]:text-[1.2rem] [&_ol]:m-0 [&_ul]:text-[1.2rem] [&_ul]:m-0 [&_li]:text-[1.2rem] [&_li]:m-0">
          <sup className="shrink-0 text-sm mt-2 opacity-70">#{identifier}</sup>
          <div className="min-w-0 [&>p:first-of-type]:mt-0 [&>p:last-of-type]:mb-0">
            {children}
          </div>
        </div>
        <a
          href={footnotesLinkWithOrigin(anchorId)}
          className="flex w-fit items-center gap-1 px-3 pb-2.5 font-sans text-[11px] text-black no-underline"
        >
          <LinkIcon size={11} />
          link to this footnote
        </a>
      </div>
    </div>,
    document.body
  );
}
